import React from 'react';
import {
  AddOperandButton,
  Filter,
  getEmptyFilterExpression,
  getRootOperator,
  OperatorType,
  RelationsProps,
  StringConditionType,
} from '@fabric-ui/components';
import { flex } from '@fabric-ui/theme';
import { emptyObject } from '@fabric-ui/utils';
import { Box } from '@mui/material';
import { ExpressionBuilder, useExpressionBuilder } from './ExpressionBuilderContext';
import { FilterConfig, useFilterConfig } from './FilterContext';
import { useExpandedGroupPaths } from './hooks/useExpandedGroupPaths';
import { useExpressionOperations } from './hooks/useExpressionOperations';
import RenderOperator from './RenderOperator';

export type ExpressionBuilderProps = NewExpressionBuilderBaseProps &
  ExpressionBuilder &
  Omit<FilterConfig, 'size'> & {
    size?: 'xSmall' | 'small' | 'medium';
    fieldSelectionLimit?: { limit: number; message: string };
  };

export interface NewExpressionBuilderBaseProps {
  filter?: Filter;
  setFilter?: (filter: Filter) => void;
  rowsLimit?: { limit: number; message: string };
}

export const NewExpressionBuilderBase: React.FC<NewExpressionBuilderBaseProps> = ({ setFilter, filter: filterProp, rowsLimit }) => {
  const {
    supportParser = false,
    defaultField = null,
    relationsProps = emptyObject as RelationsProps,
    hasListTab = false,
    readOnly = false,
    disableMessage,
    canDeleteLast,
  } = useExpressionBuilder();
  const emptyFilter = React.useMemo(
    () => getEmptyFilterExpression(null, supportParser ? StringConditionType.EQUALS : undefined),
    [supportParser]
  );
  const filter = filterProp ?? emptyFilter;
  const { expandGroup, paths: expandedGroupPaths } = useExpandedGroupPaths(filter, hasListTab);
  const { size, isVertical, fullWidth } = useFilterConfig();
  const rootOperator = getRootOperator<OperatorType.AND | OperatorType.OR>(filter);
  const oppositeOperator = rootOperator === OperatorType.OR ? OperatorType.AND : OperatorType.OR;
  // useExpressionOperations may compute an "undefined" next filter for some tree mutations (e.g.
  // deleting the last operand when `canDeleteLast` is set). Some consumers rely on receiving
  // `undefined` here to reset their own state (see EditAssetAgingCondition), so this must stay a
  // pass-through of whatever `setFilter` prop was given, not a runtime guard.
  const { addGlobal, disableAddButton, toggleOperators } = useExpressionOperations(filter, setFilter as (filter?: Filter) => void, {
    canDeleteLast,
    defaultEmptyFilter: relationsProps.defaultEmptyFilter,
    defaultField,
    hasListTab,
    rootOperator,
    rowsLimit,
    supportParser,
  });

  const onToggleLogicalOperators = readOnly ? () => {} : toggleOperators;

  return (
    <Box sx={{ ...flex.col, ...(fullWidth && !isVertical && { width: '100%' }) }}>
      <RenderOperator
        rootOperator={rootOperator}
        filter={filter}
        setFilter={setFilter}
        onToggleLogicalOperators={onToggleLogicalOperators}
        expandedGroupPaths={expandedGroupPaths}
        onExpandGroup={expandGroup}
      />
      {rowsLimit?.limit !== 1 && !readOnly && (
        <Box sx={{ ...flex.row, gap: 1, my: 1 }}>
          <AddOperandButton
            size={size}
            operator={rootOperator}
            isAdd
            onClick={() => addGlobal(rootOperator)}
            disabled={disableAddButton}
            disableMessage={disableMessage}
          />
          {filter[rootOperator]?.operands.length === 1 && (
            <AddOperandButton
              size={size}
              operator={oppositeOperator}
              isAdd
              onClick={() => addGlobal(oppositeOperator)}
              disabled={disableAddButton}
              disableMessage={disableMessage}
            />
          )}
        </Box>
      )}
    </Box>
  );
};
